import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { useMe, useTenants } from "@/api/hooks";
import { MobileDevicesTab } from "./MobileDevicesTab";
import { MobilePolicyTab } from "./MobilePolicyTab";
import { AppConfigPanel } from "./AppConfigPanel";
import { ToastViewport, useToasts } from "./Toast";

const TABS = [
    { key: "devices", label: "Devices", icon: Smartphone },
    { key: "policy", label: "App protection policy", icon: ShieldCheck },
    { key: "appconfig", label: "Managed configuration (MDM)", icon: Info }
] as const;
type TabKey = (typeof TABS)[number]["key"];

/**
 * Mobile devices & app protection — Athavita Mail / Connect / Authenticator on iOS and Android
 * (Mobile/docs/phase0-contract.md §4 "Admin", §5, §6; MOBILE_APP_MAM.md §3). Data comes from
 * the Identity service through the admin-bff (/api/admin/mobile/*).
 */
export function MobilePage() {
    const me = useMe();
    const tenants = useTenants();
    const [params, setParams] = useSearchParams();
    const rawTab = params.get("tab");
    const tab: TabKey = TABS.some((t) => t.key === rawTab) ? (rawTab as TabKey) : "devices";
    const isSuperAdmin = me.data?.isSuperAdmin ?? false;

    // "" = all tenants (offered to SuperAdmins on the Devices tab only).
    const [tenantId, setTenantId] = useState("");
    const [seeded, setSeeded] = useState(false);
    useEffect(() => {
        if (!seeded && tenants.data) {
            setTenantId(tenants.data[0]?.id ?? "");
            setSeeded(true);
        }
    }, [tenants.data, seeded]);

    const tenantNames = useMemo(
        () => new Map((tenants.data ?? []).map((t) => [t.id, t.displayName] as const)),
        [tenants.data]
    );

    const { toasts, notify, dismiss } = useToasts();

    const selectTab = (key: TabKey) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", key);
                return next;
            },
            { replace: true }
        );

    return (
        <div className="mx-auto max-w-6xl">
            <header className="mb-4 flex items-center gap-2">
                <Smartphone className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Mobile devices &amp; app protection</h1>
            </header>
            <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
                Athavita Mail, Connect and Authenticator on iPhone and Android. See every registered phone, sign a
                device out or wipe its Athavita data, and set the app protection policy — app PIN, data-leak controls
                and device health — that the apps enforce. Protection covers only the Athavita apps and their data,
                never the rest of the user&apos;s phone.
            </p>

            {tab !== "appconfig" ? (
                <div className="mb-4 max-w-sm">
                    <label htmlFor="mobile-tenant" className="mb-1 block text-xs font-medium">Tenant</label>
                    {tenants.isPending ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading tenants…
                        </div>
                    ) : (
                        <select
                            id="mobile-tenant"
                            value={tenantId}
                            onChange={(e) => setTenantId(e.target.value)}
                            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            {isSuperAdmin ? (
                                <option value="">{tab === "policy" ? "Select a tenant…" : "All tenants"}</option>
                            ) : null}
                            {(tenants.data ?? []).map((t) => (
                                <option key={t.id} value={t.id}>{t.displayName} ({t.slug})</option>
                            ))}
                        </select>
                    )}
                </div>
            ) : null}

            <div role="tablist" aria-label="Mobile sections" className="mb-4 flex flex-wrap gap-1 border-b">
                {TABS.map((t) => {
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => selectTab(t.key)}
                            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
                                active
                                    ? "border-primary font-medium text-primary"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            <t.icon className="h-4 w-4" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "devices" ? (
                <MobileDevicesTab tenantId={tenantId || null} tenantNames={tenantNames} notify={notify} />
            ) : tab === "policy" ? (
                tenantId ? (
                    <MobilePolicyTab
                        key={tenantId}
                        tenantId={tenantId}
                        tenantName={tenantNames.get(tenantId) ?? ""}
                        notify={notify}
                    />
                ) : (
                    <section className="flex items-center justify-center rounded-lg border bg-card p-10 text-sm text-muted-foreground">
                        Select a tenant to edit its app protection policy.
                    </section>
                )
            ) : (
                <AppConfigPanel />
            )}

            <ToastViewport toasts={toasts} onDismiss={dismiss} />
        </div>
    );
}
